import SyncManager from "./SyncManager";
import store from "@/store";
import { db } from "@/services/db";
import { Network } from "@capacitor/network";

// Application-facing synchronization API
export default {
    // Track first sync after page load
    _hasRunFirstSync: false,

    // Track last sync time for debouncing
    _lastSyncTime: 0,
    _SYNC_DEBOUNCE_TIME: 2000, // 2 seconds

    // Track active operation promises
    _activePromises: {
        sync_inspection: null,
        process_queue: null,
        setup_base_tables: null,
        compress_images: null,
    },

    /**
     * Initialize the sync system
     */
    init() {
        SyncManager.init();
        console.log("Sync system initialized");
    },

    /**
     * Helper to clear a promise when operation completes
     */
    _clearPromise(operationType) {
        this._activePromises[operationType] = null;
    },

    /**
     * Perform full synchronization of an inspection
     *
     * @param {string} inspection_id - ID of the inspection to synchronize
     * @returns {Promise} - Promise that resolves when sync completes
     */
    async sync(inspection_id, force = false) {
        const now = Date.now();

        // Debounce repeated sync calls unless force is true
        if (!force && now - this._lastSyncTime < this._SYNC_DEBOUNCE_TIME) {
            console.log(
                `Sync called too soon after previous sync (${Math.floor((now - this._lastSyncTime) / 1000)}s), ignoring`
            );
            return Promise.resolve({ status: "debounced", message: "Sync called too frequently" });
        }

        // Update last sync time
        this._lastSyncTime = now;

        if (force) this.stopSync();

        if (this._activePromises.sync_inspection) {
            console.log("Sync already in progress, ignoring new request");
            return Promise.resolve({ status: "already_running", message: "Sync operation already in progress" });
        }

        // Check if setup is running first
        const status = SyncManager.checkStatus();
        if (status.running && status.task?.type === "setup_base_tables") {
            console.log("Setup is running, will wait for it to complete before syncing");
            try {
                // If setup is running, wait for it to finish before proceeding
                if (this._activePromises.setup_base_tables) {
                    await this._activePromises.setup_base_tables;
                } else {
                    // Setup is running but we don't have the promise, wait briefly
                    await new Promise((resolve) => setTimeout(resolve, 3000));
                }
                console.log("Setup completed, proceeding with sync");
            } catch (error) {
                console.error("Error waiting for setup to complete:", error);
                // Continue despite error
            }
        }

        if (this._activePromises.process_queue) {
            this.stopQueue();
        }

        if (!inspection_id) {
            inspection_id = store.getters.getInspectionId;
        }
        
        // Validate inspection_id before proceeding
        if (!inspection_id || typeof inspection_id !== 'number') {
            console.log('No valid inspection ID available for sync. Only processing queue.');
            // Just process the queue without inspection sync
            try {
                await this.processQueue();
                return Promise.resolve({ 
                    status: "queue_only", 
                    message: "No inspection selected, processed queue only" 
                });
            } catch (error) {
                console.error('Error processing queue:', error);
                throw error;
            }
        }

        // Only process queue if no other operations are running
        const currentStatus = SyncManager.checkStatus();
        if (!currentStatus.running) {
            await this.processQueue();
        } else {
            console.log(`Skipping queue processing because ${currentStatus.task?.type} is running`);
        }

        store.dispatch("setSyncing", true);
        store.dispatch("setInspectionSyncProgress", 0);

        if (!store.getters.online) {
            store.dispatch("setSyncing", false);
            return Promise.reject(new Error("Cannot sync while offline"));
        }

        this._activePromises.sync_inspection = new Promise((resolve, reject) => {
            const started = SyncManager.startTask(
                "sync_inspection",
                { inspection_id, waitForPrevious: false },
                {
                    progress: (pct) => store.dispatch("setInspectionSyncProgress", pct),
                    complete: () => {
                        store.dispatch("setSyncing", false);
                        // Don't automatically call setup() here - this creates a loop
                        // this.setup();
                        this._clearPromise("sync_inspection");
                        resolve();
                    },
                    error: (error) => {
                        store.dispatch("setSyncing", false);
                        this._clearPromise("sync_inspection");
                        reject(error);
                    },
                    stop: () => {
                        store.dispatch("setSyncing", false);
                        this._clearPromise("sync_inspection");
                        resolve();
                    },
                    timeout: () => {
                        store.dispatch("setSyncing", false);
                        this._clearPromise("sync_inspection");
                        reject(new Error("Sync timed out"));
                    },
                }
            );

            if (!started) {
                store.dispatch("setSyncing", false);
                this._clearPromise("sync_inspection");
                reject(new Error("Failed to start sync operation"));
            }
        });

        return this._activePromises.sync_inspection;
    },

    /**
     * Process queue with timeout protection
     */
    async processQueueWithTimeout(timeoutMs = 30000) {
        if (this._activePromises.process_queue) {
            console.log("Queue already processing, ignoring new request in processQueueWithTimeout");
            return Promise.resolve({ status: "already_running", message: "Queue processing already in progress" });
        }

        if (this._activePromises.sync_inspection) {
            console.log("Sync operation is running, ignoring queue request");
            return Promise.resolve({ status: "sync_running", message: "Sync operation in progress, queue delayed" });
        }

        // Start wrapper promise and store it
        const wrapper = new Promise((resolve) => {
            const timeoutId = setTimeout(() => {
                this._clearPromise("process_queue");
                resolve({ status: "timeout", message: "Queue processing timed out" });
            }, timeoutMs);

            this.processQueue()
                .then((result) => {
                    clearTimeout(timeoutId);
                    resolve({ status: "success", result });
                })
                .catch((error) => {
                    clearTimeout(timeoutId);
                    resolve({ status: "error", message: error.message });
                });
        });

        this._activePromises.process_queue = wrapper;
        return wrapper;
    },

    /**
     * Process the queue
     */
    async processQueue() {
        if (this._activePromises.process_queue) {
            console.log("Queue already processing, restarting");
            this.stopQueue();
            await new Promise((r) => setTimeout(r, 100));
        }

        const status = SyncManager.checkStatus();
        if (status.running && status.task?.type !== "process_queue") {
            console.log(`Cannot process queue while ${status.task?.type} is running, ignoring`);
            return Promise.resolve({
                status: "blocked_by_other_task",
                message: `Cannot process queue while ${status.task?.type} is running`,
            });
        }

        store.dispatch("setQueue", true);
        store.dispatch("setQueueProgress", 0);

        this._activePromises.process_queue = new Promise((resolve, reject) => {
            const started = SyncManager.startTask(
                "process_queue",
                { waitForPrevious: false },
                {
                    progress: (pct) => store.dispatch("setQueueProgress", pct),
                    complete: () => {
                        store.dispatch("setQueue", false);
                        this._clearPromise("process_queue");
                        resolve({ status: "success" });
                    },
                    error: (error) => {
                        store.dispatch("setQueue", false);
                        this._clearPromise("process_queue");
                        reject(error);
                    },
                    timeout: () => {
                        store.dispatch("setQueue", false);
                        this._clearPromise("process_queue");
                        reject(new Error("Queue processing timed out"));
                    },
                }
            );

            if (!started) {
                store.dispatch("setQueue", false);
                this._clearPromise("process_queue");
                reject(new Error("Failed to start queue processing"));
            }
        });

        return this._activePromises.process_queue;
    },

    /**
     * Process queue with specific parameters
     */
    async processQueueWithParams(params = {}) {
        console.log("Processing queue with custom parameters:", params);

        if (this._activePromises.process_queue) {
            console.log("Queue already processing, restarting");
            this.stopQueue();
            await new Promise((r) => setTimeout(r, 100));
        }

        store.dispatch("setQueue", true);
        store.dispatch("setQueueProgress", 0);

        this._activePromises.process_queue = new Promise((resolve, reject) => {
            const started = SyncManager.startTask(
                "process_queue",
                { ...params },
                {
                    progress: (pct) => store.dispatch("setQueueProgress", pct),
                    complete: () => {
                        store.dispatch("setQueue", false);
                        this._clearPromise("process_queue");
                        resolve({ status: "success" });
                    },
                    error: (error) => {
                        store.dispatch("setQueue", false);
                        this._clearPromise("process_queue");
                        reject(error);
                    },
                    timeout: () => {
                        store.dispatch("setQueue", false);
                        this._clearPromise("process_queue");
                        reject(new Error("Queue processing timed out"));
                    },
                }
            );

            if (!started) {
                store.dispatch("setQueue", false);
                this._clearPromise("process_queue");
                reject(new Error("Failed to start queue processing"));
            }
        });

        return this._activePromises.process_queue;
    },

    // Flag to track setup in progress state
    _setupInProgress: false,
    _setupTimeoutId: null,

    /**
     * Setup base tables
     */
    async setup() {
        console.log("Setting up base tables");

        // First check promise - this is the most reliable indicator
        if (this._activePromises.setup_base_tables) {
            console.log("Setup already in progress (promise), ignoring new request");
            return Promise.resolve({ status: "already_running", message: "Setup already in progress (promise)" });
        }

        // Check flag as a secondary indicator
        if (this._setupInProgress) {
            console.log("Setup flag is true, but no active promise. Resetting and continuing.");
            this._setupInProgress = false;
            clearTimeout(this._setupTimeoutId);
        }

        // Set flag to prevent concurrent setups
        this._setupInProgress = true;

        // Safety timeout - force reset flag after 5 minutes to prevent stuck state
        clearTimeout(this._setupTimeoutId);
        this._setupTimeoutId = setTimeout(() => {
            if (this._setupInProgress && !this._activePromises.setup_base_tables) {
                console.log("Safety timeout triggered - resetting stuck setup flag");
                this._setupInProgress = false;
            }
        }, 5 * 60 * 1000); // 5-minute safety timeout

        const status = SyncManager.checkStatus();
        if (status.running && status.task?.type !== "setup_base_tables") {
            console.log(`Cannot start setup while ${status.task?.type} is running, ignoring`);
            this._setupInProgress = false; // Reset flag
            return Promise.resolve({
                status: "blocked_by_other_task",
                message: `Cannot start setup while ${status.task?.type} is running`,
            });
        }

        try {
            this._activePromises.setup_base_tables = new Promise((resolve, reject) => {
                const started = SyncManager.startTask(
                    "setup_base_tables",
                    {},
                    {
                        complete: () => {
                            console.log("Base tables setup completed");
                            this._clearPromise("setup_base_tables");
                            this._setupInProgress = false; // Reset flag on completion
                            clearTimeout(this._setupTimeoutId); // Clear safety timeout

                            resolve({ status: "success" });
                        },
                        error: (error) => {
                            console.error("Base tables setup failed:", error);
                            this._clearPromise("setup_base_tables");
                            this._setupInProgress = false; // Reset flag on error
                            clearTimeout(this._setupTimeoutId); // Clear safety timeout
                            reject(error);
                        },
                        timeout: () => {
                            console.error("Base tables setup timed out");
                            this._clearPromise("setup_base_tables");
                            this._setupInProgress = false; // Reset flag on timeout
                            clearTimeout(this._setupTimeoutId); // Clear safety timeout
                            reject(new Error("Setup timed out"));
                        },
                    }
                );

                if (!started) {
                    this._clearPromise("setup_base_tables");
                    this._setupInProgress = false; // Reset flag if not started
                    clearTimeout(this._setupTimeoutId); // Clear safety timeout
                    reject(new Error("Failed to start setup operation"));
                }
            });

            return this._activePromises.setup_base_tables;
        } catch (error) {
            // Ensure flag is reset if any errors occur
            this._setupInProgress = false;
            clearTimeout(this._setupTimeoutId); // Clear safety timeout
            throw error;
        }
    },

    /**
     * Compress and save images
     */
    async compressAndSaveImages(
        images,
        upload_url,
        imageable_uuid,
        addTimestamp = false,
        addInspectorName = false,
        inspectorName = null,
        cropToSquare = false
    ) {
        if (!Array.isArray(images) || images.length === 0) {
            console.warn("No images provided for compression");
            return Promise.resolve({ status: "no_images" });
        }

        console.log(`Compressing ${images.length} images`);

        if (this._activePromises.compress_images) {
            console.log("Image compression already in progress, waiting");
            try {
                await this._activePromises.compress_images;
            } catch {
                console.warn("Previous compression failed, proceeding");
            }
        }

        this._activePromises.compress_images = new Promise((resolve, reject) => {
            const started = SyncManager.startTask(
                "compress_images",
                {
                    images,
                    upload_url,
                    imageable_uuid,
                    addTimestamp,
                    addInspectorName,
                    inspectorName,
                    cropToSquare,
                    waitForPrevious: false,
                },
                {
                    complete: () => {
                        this._clearPromise("compress_images");
                        resolve({ status: "success", count: images.length });
                    },
                    error: (error) => {
                        this._clearPromise("compress_images");
                        reject(error);
                    },
                    timeout: () => {
                        this._clearPromise("compress_images");
                        reject(new Error("Image compression timed out"));
                    },
                }
            );

            if (!started) {
                this._clearPromise("compress_images");
                reject(new Error("Failed to start image compression"));
            }
        });

        return this._activePromises.compress_images;
    },

    /**
     * Check sync system health
     */
    checkHealth() {
        const status = SyncManager.checkStatus();
        return {
            ...status,
            activeOperations: Object.entries(this._activePromises)
                .filter(([_, p]) => p !== null)
                .map(([type]) => type),
            taskChunkSize: SyncManager.taskChunkSize || 100,
        };
    },

    /**
     * Stop the queue
     */
    stopQueue() {
        console.log("stopQueue() called");
        const status = SyncManager.checkStatus();
        if (status.running && status.task?.type === "process_queue") {
            console.log("Forcefully stopping queue task");
            SyncManager.stopTask("User requested stop queue");
        }
        this._clearPromise("process_queue");
        store.dispatch("setQueue", false);
        store.dispatch("setQueueProgress", 0);
    },

    /**
     * Stop synchronization
     */
    stopSync() {
        console.log("stopSync() called");
        if (SyncManager.checkStatus().task?.type === "sync_inspection") {
            SyncManager.stopTask("User requested stop sync");
        }
        this._clearPromise("sync_inspection");
        store.dispatch("setSyncing", false);
        store.dispatch("setInspectionSyncProgress", 0);
    },

    /**
     * Stop setup
     */
    stopSetup() {
        console.log("stopSetup() called");
        if (SyncManager.checkStatus().task?.type === "setup_base_tables") {
            SyncManager.stopTask("User requested stop setup");
        }
        this._clearPromise("setup_base_tables");
        this._setupInProgress = false; // Reset flag on manual stop
        clearTimeout(this._setupTimeoutId); // Clear safety timeout
    },

    /**
     * Stop all tasks
     */
    stopAll() {
        console.log("stopAll() called");
        const status = SyncManager.checkStatus();
        if (status.running) {
            console.log(`Forcefully stopping ${status.task?.type} task`);
            SyncManager.stopTask("User requested stop all");
        }
        Object.keys(this._activePromises).forEach((key) => (this._activePromises[key] = null));
        this._setupInProgress = false; // Reset setup flag
        clearTimeout(this._setupTimeoutId); // Clear safety timeout
        store.dispatch("setQueue", false);
        store.dispatch("setSyncing", false);
        store.dispatch("setInspectionSyncProgress", 0);
        store.dispatch("setQueueProgress", 0);
    },
};
